import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, Crosshair, MapPin, Lock, Pencil, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const NOMINATIM = "https://nominatim.openstreetmap.org";
// Default GPS for new tickets when no coordinates are saved yet
// (e.g. office/base location). Existing tickets always use their own lat/lng.
const DEFAULT_CENTER = [-7.205848349283519, 109.82737096958913];
const DEFAULT_ZOOM = 17;
const PIN_ZOOM = 17;
const DEBOUNCE_MS = 700;

async function reverseGeocode(lat, lng) {
  const url = `${NOMINATIM}/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=id`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error("reverse-fail");
  return await res.json();
}

/**
 * Derive a canonical "Wilayah" (region) name from a Nominatim address object.
 * Priority: any component containing "Batur" → "Batur". Otherwise: Desa Utama
 * (village/town/suburb) or Kecamatan (city_district) as fallback.
 */
function resolveWilayah(addr) {
  if (!addr) return "";
  const batWatch = ["village", "town", "city_district", "suburb", "county", "neighbourhood", "quarter", "hamlet", "municipality"];
  for (const k of batWatch) {
    const v = addr[k];
    if (typeof v === "string" && /\bbatur\b/i.test(v)) return "Batur";
  }
  const priority = ["village", "town", "suburb", "city_district", "municipality", "county", "city"];
  for (const k of priority) {
    const v = addr[k];
    if (typeof v === "string" && v.trim()) {
      return v.replace(/^(Desa|Kelurahan|Kecamatan|Kabupaten|Kota)\s+/i, "").trim();
    }
  }
  return "";
}

// Sync map center to external lat/lng props ONLY when the change is external
// (e.g. "Lokasi Saya" or a fresh initial value), not when it's the feedback loop
// from a user drag (moveend → reverse-geocode → onChange → prop change).
// Zoom is PRESERVED — we never change zoom implicitly.
function ExternalCenterSync({ lat, lng, silentRef }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat !== "number" || typeof lng !== "number" || isNaN(lat) || isNaN(lng)) return;
    const cur = map.getCenter();
    const dLat = Math.abs(cur.lat - lat);
    const dLng = Math.abs(cur.lng - lng);
    // Threshold ~0.001° (≈ 100m at equator). Drag-induced round-trip diffs are
    // effectively zero; only genuine external moves cross this bar.
    if (dLat < 0.001 && dLng < 0.001) return;
    silentRef.current = true;
    // IMPORTANT: preserve the user's current zoom — never bump it up.
    map.setView([lat, lng], map.getZoom(), { animate: false });
    setTimeout(() => { silentRef.current = false; }, 80);
  }, [lat, lng, map, silentRef]);
  return null;
}

// Listen to user-initiated map movement and debounce a callback with new center.
function CenterWatcher({ onCenterChanged, silentRef }) {
  const timerRef = useRef(null);
  useMapEvents({
    moveend(e) {
      if (silentRef.current) return;
      const c = e.target.getCenter();
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onCenterChanged(c.lat, c.lng);
      }, DEBOUNCE_MS);
    },
  });
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);
  return null;
}

// Reactively enable/disable Leaflet's interaction handlers when the `active`
// prop flips. `MapContainer` only applies its interaction props at initial
// mount; toggling them via re-render is a no-op inside Leaflet — the actual
// handlers stay in whatever state they were when the map booted. This effect
// bridges that gap so "Ubah Lokasi" truly unlocks the map.
function InteractionToggle({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const setEnabled = (h, on) => {
      if (!h) return;
      if (on) h.enable(); else h.disable();
    };
    setEnabled(map.dragging, active);
    setEnabled(map.scrollWheelZoom, active);
    setEnabled(map.touchZoom, active);
    setEnabled(map.doubleClickZoom, active);
    setEnabled(map.boxZoom, active);
    setEnabled(map.keyboard, active);
    if (map.tap) setEnabled(map.tap, active);
    if (map.zoomControl) {
      try {
        if (active) map.zoomControl.addTo(map);
        else map.zoomControl.remove();
      } catch (_) { /* zoomControl may not exist depending on options */ }
    }
  }, [active, map]);
  return null;
}


/**
 * LocationPicker — fixed center-pin map picker (Google Maps-style).
 *
 * Props:
 *   value: string       (currently detected/saved wilayah, shown in read-only textbox)
 *   lat, lng: numbers   (saved coordinates; if provided, map opens centered here)
 *   onChange: ({ lat, lng, wilayah }) => void
 *   disabled: boolean   (view-only mode; map still renders but cannot be moved)
 *
 * Behavior:
 *   - Pin is fixed at the visual center of the map (not draggable).
 *   - The map is dragged/zoomed; the underlying coordinate is the map center.
 *   - After user stops moving the map for DEBOUNCE_MS, we reverse-geocode
 *     the new center and update wilayah + saved lat/lng.
 *   - Existing tickets: initial center = saved lat/lng, no reverse-geocode fires.
 *   - Scroll-wheel zoom on desktop, pinch-zoom on mobile (Leaflet defaults).
 */
export default function LocationPicker({ value = "", lat, lng, onChange, disabled = false }) {
  const [reverseLoading, setReverseLoading] = useState(false);
  const silentRef = useRef(false);
  const abortRef = useRef(null);
  // Default LOCKED: the map cannot be panned/zoomed by scroll/touch. Users
  // opt into editing via the "Ubah Lokasi" button. This prevents accidental
  // coordinate changes when scrolling the form on desktop or mobile.
  const [editing, setEditing] = useState(false);
  // Snapshot at edit-enter so "Batal" can restore the exact pre-edit state.
  const preEditRef = useRef(null);

  const hasPin = typeof lat === "number" && typeof lng === "number" && !isNaN(lat) && !isNaN(lng);

  // "Dirty" when editing has caused coord drift from the pre-edit snapshot.
  // preEditRef is a ref so it never triggers a re-render on its own — but the
  // moment the map is dragged, CenterWatcher → runReverseGeocode → onChange →
  // parent re-renders this component with new lat/lng. On that render we read
  // the still-live snapshot and compare. On Simpan/Batal preEditRef is cleared
  // (or coords are restored), so dirty naturally drops to false.
  const preSnap = preEditRef.current;
  const dirty = editing && !!preSnap && (preSnap.lat !== lat || preSnap.lng !== lng);

  // Initial center resolution: existing ticket coords > device GPS > fallback office GPS.
  // For tickets WITHOUT coords we probe geolocation exactly once at mount before
  // rendering the map, so it opens near the technician's device location if allowed.
  // Note: GPS is used only as the initial camera position — it is NOT auto-saved
  // as the ticket location; the user must drag the map to commit coords.
  const [resolvedCenter, setResolvedCenter] = useState(hasPin ? [lat, lng] : null);
  const initialZoom = PIN_ZOOM;

  useEffect(() => {
    // If existing ticket already has coords, we're already set — do nothing.
    if (hasPin) return;
    // In view-only mode, skip device-GPS probe (no interaction expected).
    if (disabled) { setResolvedCenter(DEFAULT_CENTER); return; }
    let cancelled = false;
    const fallback = () => { if (!cancelled) setResolvedCenter(DEFAULT_CENTER); };
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      fallback();
      return;
    }
    // Hard timeout guard so the map never stays blank for long.
    const t = setTimeout(fallback, 4500);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        clearTimeout(t);
        setResolvedCenter([pos.coords.latitude, pos.coords.longitude]);
      },
      () => {
        if (cancelled) return;
        clearTimeout(t);
        fallback();
      },
      { enableHighAccuracy: true, timeout: 4000, maximumAge: 60000 }
    );
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Run ONCE per dialog session, per rule #5 (no background tracking)

  const runReverseGeocode = async (nLat, nLng) => {
    if (disabled) return;
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setReverseLoading(true);
    try {
      const r = await reverseGeocode(nLat, nLng);
      if (ac.signal.aborted) return;
      const wil = resolveWilayah(r?.address);
      // If geocode couldn't resolve a wilayah, keep the previous one.
      // `geocoded` flag signals to parents (Feb 2026 fix) whether this
      // change resolved a fresh wilayah for the user-chosen coordinate.
      onChange?.({ lat: nLat, lng: nLng, wilayah: wil || value, geocoded: !!wil });
    } catch {
      // Preserve previous wilayah on failure, still save coords the user chose.
      toast.warning("Wilayah otomatis gagal dimuat — koordinat tetap disimpan.");
      onChange?.({ lat: nLat, lng: nLng, wilayah: value, geocoded: false });
    } finally {
      if (!ac.signal.aborted) setReverseLoading(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Peramban tidak mendukung geolokasi");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => runReverseGeocode(pos.coords.latitude, pos.coords.longitude),
      (err) => {
        const msg = err?.code === 1
          ? "Izin lokasi ditolak. Aktifkan di pengaturan peramban."
          : "Tidak dapat mendapatkan lokasi Anda saat ini.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      {/* Read-only wilayah textbox (no manual editing, no dropdown) */}
      <div className="relative">
        <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none" />
        <input
          value={value || ""}
          readOnly
          disabled={disabled}
          tabIndex={-1}
          className="w-full h-10 pl-9 pr-9 rounded-md border border-[#E5E7EB] bg-[#F8F9FA] cursor-default text-sm text-[#0A0A0A] focus:outline-none"
          placeholder="Wilayah terisi otomatis setelah menggeser peta"
          data-testid="loc-wilayah-readonly"
          aria-readonly="true"
        />
        {reverseLoading && (
          <Loader2
            className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#0055FF] animate-spin"
            data-testid="loc-reverse-loading"
            aria-label="Memuat wilayah"
          />
        )}
      </div>

      {/* Map with center-fixed pin overlay */}
      <div
        className="relative rounded-md overflow-hidden border border-[#E5E7EB] bg-[#F8F9FA]"
        data-testid="loc-map-wrap"
        style={{ zIndex: 0 }}
      >
        <div className="h-64 md:h-72 w-full" data-testid="loc-map">
          {resolvedCenter ? (
            <MapContainer
              center={resolvedCenter}
              zoom={initialZoom}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={!disabled && editing}
              dragging={!disabled && editing}
              touchZoom={!disabled && editing}
              doubleClickZoom={!disabled && editing}
              zoomControl={!disabled && editing}
              attributionControl={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <InteractionToggle active={!disabled && editing} />
              <ExternalCenterSync lat={lat} lng={lng} silentRef={silentRef} />
              {!disabled && editing && (
                <CenterWatcher onCenterChanged={runReverseGeocode} silentRef={silentRef} />
              )}
            </MapContainer>
          ) : (
            <div
              className="h-full w-full flex items-center justify-center bg-[#F8F9FA] text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF] gap-2"
              data-testid="loc-map-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              Mengambil lokasi perangkat…
            </div>
          )}
        </div>

        {/* Fixed pin — visually anchored at map center (tip of the pin) */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full"
          style={{ zIndex: 500 }}
          data-testid="loc-center-pin"
          aria-hidden
        >
          <svg
            width="36"
            height="46"
            viewBox="0 0 32 42"
            xmlns="http://www.w3.org/2000/svg"
            style={{ filter: "drop-shadow(0 3px 4px rgba(0,0,0,0.35))" }}
          >
            <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 26 16 26s16-14 16-26C32 7.2 24.8 0 16 0z" fill="#0055FF" />
            <circle cx="16" cy="16" r="6" fill="#fff" />
          </svg>
        </div>
        {/* Precise center dot */}
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{ zIndex: 501 }}
          aria-hidden
        >
          <div className="h-1.5 w-1.5 rounded-full bg-[#0055FF] ring-2 ring-white shadow" />
        </div>

        {/* Bottom-left coordinate badge (kept in place; separate from action controls).
            When editing causes drift from the pre-edit snapshot, an amber
            "Koordinat berubah" chip appears above it as a subtle affordance
            that the change is unsaved until Simpan Lokasi is pressed. */}
        <div
          className="absolute bottom-2 left-2 pointer-events-none flex flex-col items-start gap-1"
          style={{ zIndex: 502 }}
        >
          {dirty && (
            <div
              className="inline-flex items-center gap-1 bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest shadow-sm"
              data-testid="loc-dirty-badge"
              role="status"
              aria-live="polite"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden />
              Koordinat berubah
            </div>
          )}
          <div className="bg-white/95 backdrop-blur px-2 py-1 rounded text-[10px] font-mono-tab text-[#4B5563] shadow-sm">
            {hasPin ? (
              <>
                <span className="font-bold text-[#0A0A0A]">Lat</span> {lat.toFixed(6)}{"  "}
                <span className="font-bold text-[#0A0A0A]">Lng</span> {lng.toFixed(6)}
              </>
            ) : (
              <span className="italic text-[#9CA3AF]">
                {editing ? "Geser peta untuk memilih lokasi" : "Belum ada koordinat"}
              </span>
            )}
          </div>
        </div>

        {/* Top-right LOCKED pill only. During EDITING the action buttons
            (Lokasi Saya / Batal / Simpan) move out of the map area to a
            row below so the picker isn't obstructed while the technician
            drags to align the marker. */}
        {!disabled && !editing && (
          <div
            className="absolute top-2 right-2 pointer-events-auto max-w-[calc(100%-1rem)]"
            style={{ zIndex: 502 }}
          >
            <div
              className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md p-1"
              data-testid="loc-control-locked"
            >
              <span
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#F3F4F6] text-[#6B7280] text-[10px] font-bold uppercase tracking-widest"
                data-testid="loc-lock-badge"
                aria-live="polite"
              >
                <Lock className="h-3.5 w-3.5" aria-hidden />
                Terkunci
              </span>
              <button
                type="button"
                onClick={() => {
                  preEditRef.current = { lat, lng, wilayah: value };
                  setEditing(true);
                }}
                className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md bg-[#0055FF] hover:bg-[#0044CC] active:bg-[#003AB3] text-white text-[10px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] focus-visible:ring-offset-1"
                data-testid="loc-unlock"
                aria-label="Ubah lokasi"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Ubah Lokasi
              </button>
            </div>
          </div>
        )}
      </div>

      {!disabled && (
        <p className="text-[10px] text-[#9CA3AF] italic" data-testid="loc-hint">
          {editing
            ? "Geser peta hingga pin di tengah menunjuk lokasi target. Tekan Simpan Lokasi setelah selesai."
            : "Peta terkunci untuk mencegah perubahan koordinat saat scroll. Tekan Ubah Lokasi untuk mengedit."}
        </p>
      )}

      {/* EDITING action bar — placed below the map (not overlayed) so the
          marker + surrounding streets are never covered while choosing a
          location. Only shown in EDITING mode; LOCKED mode already has its
          tiny pill inside the map. */}
      {!disabled && editing && (
        <div
          className="flex flex-wrap items-center justify-end gap-2"
          data-testid="loc-control-editing"
        >
          <button
            type="button"
            onClick={useMyLocation}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-white hover:bg-[#F0F4FF] active:bg-[#E4ECFF] text-[#0055FF] border border-[#DBE4FF] text-[11px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] focus-visible:ring-offset-1"
            data-testid="loc-me"
            aria-label="Gunakan lokasi saya"
          >
            <Crosshair className="h-4 w-4" aria-hidden />
            Lokasi Saya
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => {
              // Restore pre-edit snapshot so accidental pans are undone.
              const snap = preEditRef.current;
              if (snap && (snap.lat !== lat || snap.lng !== lng || snap.wilayah !== value)) {
                onChange?.({ lat: snap.lat, lng: snap.lng, wilayah: snap.wilayah, geocoded: !!snap.wilayah });
              }
              preEditRef.current = null;
              setEditing(false);
            }}
            className="inline-flex items-center h-9 px-3 rounded-md bg-white hover:bg-[#F8F9FA] active:bg-[#F0F1F3] text-[#4B5563] border border-[#E5E7EB] text-[11px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9CA3AF] focus-visible:ring-offset-1"
            data-testid="loc-cancel"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={() => {
              // Commit = simply re-lock; onChange has already been flushed
              // via CenterWatcher during editing.
              preEditRef.current = null;
              setEditing(false);
            }}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#065F46] hover:bg-[#064E3B] active:bg-[#053E31] text-white text-[11px] font-bold uppercase tracking-widest transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#065F46] focus-visible:ring-offset-1"
            data-testid="loc-save"
            aria-label="Simpan lokasi"
          >
            <Check className="h-4 w-4" aria-hidden />
            Simpan Lokasi
          </button>
        </div>
      )}
    </div>
  );
}
